import { NodeChange, NodePositionChange } from '@xyflow/react';

import { Node, TileNode } from '@/components/diagrams/FlowChart';

import { EntityDiagramActionFn, NodesChangedAction } from '../types';

/**
 * @description Sets the `isNewTile` flag to false for nodes that are in the graveyard and have been dragged
 */
function removeNodesFromGraveyard(changes: NodeChange[], nodes: Node[]) {
  const positionChangesByNodeId = changes.reduce(
    (acc, change) => {
      if (change.type !== 'position') return acc;
      acc[change.id] = change;
      return acc;
    },
    {} as Record<string, NodePositionChange>
  );

  return nodes.map((n) => {
    const change = positionChangesByNodeId[n.id];
    if (!change) return n;
    const draggedNode = n;
    return {
      ...draggedNode,
      data: { ...draggedNode.data, isNewTile: false },
    } as TileNode;
  });
}

// NodeResizer marks its dimensions changes with a `resizing` flag (true while
// dragging, false on release); ReactFlow's measurement pass leaves it undefined.
function isResizeBatch(changes: NodeChange[]): boolean {
  return changes.some(
    (change) =>
      change.type === 'dimensions' && typeof change.resizing === 'boolean'
  );
}

// Mirror the resized ReactFlow width/height onto numeric data.width/height so
// the Tile renders the explicit size instead of its auto-width default. The
// `selected` gate covers multi-select resize; dimensionsFinalized clips icons.
function applyResizeToSelectedTiles(nodes: Node[]): Node[] {
  return nodes.map((node) => {
    if (node.type !== 'tile' || !node.selected) return node;
    // Only commit positive dimensions. Save/restore and the default-width pass
    // treat 0 as "unset" (a zero-size node freezes ReactFlow), so a transient
    // 0 from the resizer must not be written onto data.width/height.
    if (
      typeof node.width !== 'number' ||
      node.width <= 0 ||
      typeof node.height !== 'number' ||
      node.height <= 0
    ) {
      return node;
    }
    return {
      ...node,
      data: {
        ...node.data,
        width: node.width,
        height: node.height,
        dimensionsFinalized: true,
      },
    } as TileNode;
  });
}

export const nodesChangedAction: EntityDiagramActionFn<NodesChangedAction> = (
  state,
  { nodes, changes, isInteractive },
  _queueEffect
) => {
  const graveyardCleared = removeNodesFromGraveyard(changes, nodes);
  const newNodes = isResizeBatch(changes)
    ? applyResizeToSelectedTiles(graveyardCleared)
    : graveyardCleared;

  // Check if this is only a position change
  const isOnlyPositionChange = changes.every(
    (change) => change.type === 'position' || change.type === 'select'
  );

  // If it's only position changes and we're in edit mode, update the node configurations
  if (isOnlyPositionChange && isInteractive) {
    const nodeConfigurations = newNodes.flatMap((node) => {
      if (node.type !== 'tile') return [];

      return {
        nodeID: node.id,
        xPosition: node.position.x,
        yPosition: node.position.y,
      };
    });

    return {
      ...state,
      nodes: newNodes,
      nodeConfigurations,
    };
  }

  return {
    ...state,
    nodes: newNodes,
  };
};
